/**
 * DemoGuardedButton — Mutation-Guard fuer Demo-Modus (W3.1)
 *
 * Wraps Buttons die im Demo-Modus deaktiviert sein sollen.
 * - disabled im Demo-Modus
 * - Tooltip "Im Demo-Modus nicht verfuegbar"
 * - Klick auf disabled: zeigt Toast-Hinweis
 */
import { useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDemoMode } from '../hooks/useDemoMode'

interface DemoGuardedButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode
}

export function DemoGuardedButton({ onClick, children, disabled, title, style, ...rest }: DemoGuardedButtonProps) {
  const { t } = useTranslation('components')
  const { isDemoMode: demoActive } = useDemoMode()
  // F-R6-2: QA-Schalter — VITE_ALLOW_DEMO_MUTATIONS=true hebt die Demo-Sperre
  // fuer lokale Browsertests von Mutationen auf. Niemals in Produktion setzen.
  const isDemoMode = demoActive && import.meta.env.VITE_ALLOW_DEMO_MUTATIONS !== 'true'
  const toastRef = useRef<HTMLDivElement | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showDemoToast = useCallback(() => {
    // Toast inline anzeigen (kein globaler Toast-Service noetig)
    if (toastRef.current) return // bereits sichtbar

    const toast = document.createElement('div')
    toast.setAttribute('role', 'alert')
    toast.setAttribute('data-testid', 'demo-guard-toast')
    toast.textContent = t('demoGuardedButton.toast')
    Object.assign(toast.style, {
      position: 'fixed',
      bottom: '24px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'var(--surface-raised, #1e1e2e)',
      color: 'var(--text)',
      border: '1px solid var(--border)',
      padding: '10px 20px',
      borderRadius: '8px',
      zIndex: '9999',
      fontSize: '14px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
      whiteSpace: 'nowrap',
    })
    document.body.appendChild(toast)
    toastRef.current = toast

    timerRef.current = setTimeout(() => {
      toast.remove()
      toastRef.current = null
    }, 3000)
  }, [])

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isDemoMode) {
        e.preventDefault()
        showDemoToast()
        return
      }
      onClick?.(e)
    },
    [isDemoMode, onClick, showDemoToast],
  )

  return (
    <button
      {...rest}
      disabled={isDemoMode || disabled}
      title={isDemoMode ? t('demoGuardedButton.disabledTitle') : title}
      style={style}
      onClick={handleClick}
    >
      {children}
    </button>
  )
}
